(() => {
  const activeGallerySelector = 'media-gallery[data-bf-gallery-active="true"]';
  const warmedTileSources = new Set();
  let preloadScheduled = false;

  const nextRevision = (productInfo) => {
    const revision = Number(productInfo.dataset.bfGalleryRevision || 0) + 1;
    productInfo.dataset.bfGalleryRevision = String(revision);
    return revision;
  };

  const reveal = (productInfo, revision) => {
    if (revision && Number(productInfo.dataset.bfGalleryRevision) !== revision) return;
    productInfo.classList.remove('bf-gallery-is-loading');
  };

  const canPreloadTiles = () => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return true;
    return !connection.saveData && !/(^|-)2g/.test(connection.effectiveType || '');
  };

  const warmTileImages = () => {
    if (!canPreloadTiles()) return;

    const images = [];
    document.querySelectorAll('script[data-bf-gallery-tile-preloads]').forEach((node) => {
      try {
        const entries = JSON.parse(node.textContent || '[]');
        entries.forEach((entry) => {
          if (entry?.src && !images.some((image) => image.src === entry.src)) images.push(entry);
        });
      } catch (_) {
        // Invalid optional preload data must never affect the product gallery.
      }
    });

    images.slice(0, 8).forEach((entry) => {
      if (warmedTileSources.has(entry.src)) return;
      warmedTileSources.add(entry.src);
      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = 'low';
      if (entry.sizes) image.sizes = entry.sizes;
      if (entry.srcset) image.srcset = entry.srcset;
      image.src = entry.src;
    });
  };

  const scheduleTilePreload = () => {
    if (preloadScheduled || !canPreloadTiles()) return;
    preloadScheduled = true;
    const run = () => {
      preloadScheduled = false;
      warmTileImages();
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 1500 });
    } else {
      window.setTimeout(run, 600);
    }
  };

  window.BFGallery = {
    begin(productInfo) {
      if (!productInfo.querySelector(activeGallerySelector)) return;
      nextRevision(productInfo);
      productInfo.classList.add('bf-gallery-is-loading');
    },

    replace(productInfo, html) {
      const sourceGallery = productInfo.querySelector('media-gallery');
      const destinationGallery = html.querySelector('media-gallery');
      if (!sourceGallery || !destinationGallery) return false;
      // A BF gallery and its native fallback have different markup and cached
      // element references. Replace the whole gallery in either direction.
      if (!sourceGallery.matches(activeGallerySelector) && !destinationGallery.matches(activeGallerySelector)) return false;

      const revision = Number(productInfo.dataset.bfGalleryRevision || 0) || nextRevision(productInfo);
      const newGallery = document.importNode(destinationGallery, true);
      sourceGallery.replaceWith(newGallery);

      const refreshThumbnailTray = () => {
        const tray = newGallery.querySelector('[id^="GalleryThumbnails"]');
        const list = tray?.querySelector('[id^="Slider-Thumbnails"]');
        if (!tray || !list) return;

        list.querySelectorAll('.thumbnail-list__item').forEach((item) => {
          item.hidden = false;
          item.style.removeProperty('display');
          const image = item.querySelector('img');
          if (image) {
            image.loading = 'eager';
            image.decoding = 'async';
          }
        });

        tray.initPages?.();
        // Hidden mobile trays and single-image galleries have no visible slides.
        if (tray.sliderItemsToShow?.length && tray.sliderItemsToShow[0].clientWidth > 0) {
          tray.update?.();
          tray.setSlideVisibility?.();
        }
      };

      requestAnimationFrame(() => {
        refreshThumbnailTray();
        requestAnimationFrame(refreshThumbnailTray);
        window.setTimeout(refreshThumbnailTray, 180);
      });

      const currentModalContent = productInfo.productModal?.querySelector('.product-media-modal__content');
      const newModalContent = html.querySelector('product-modal .product-media-modal__content');
      if (currentModalContent && newModalContent) currentModalContent.innerHTML = newModalContent.innerHTML;

      const selectedMediaId = newGallery.dataset.bfSelectedMediaId
        || newGallery.querySelector('.product__media-item.is-active')?.dataset.mediaId
        || newGallery.querySelector('.product__media-item[data-media-id]')?.dataset.mediaId;
      const selectedMedia = selectedMediaId
        ? newGallery.querySelector(`[data-media-id="${CSS.escape(selectedMediaId)}"]`)
        : null;
      if (selectedMedia) {
        newGallery.querySelectorAll('.product__media-item.is-active').forEach((item) => item.classList.remove('is-active'));
        selectedMedia.classList.add('is-active');
      }

      const firstImage = selectedMediaId
        ? newGallery.querySelector(`[data-media-id="${CSS.escape(selectedMediaId)}"] img`)
        : newGallery.querySelector('img');
      if (!firstImage) {
        reveal(productInfo, revision);
        return true;
      }

      const ready = firstImage.complete
        ? firstImage.decode?.() || Promise.resolve()
        : new Promise((resolve) => {
            firstImage.addEventListener('load', resolve, { once: true });
            firstImage.addEventListener('error', resolve, { once: true });
          });

      Promise.resolve(ready)
        .catch(() => undefined)
        .finally(() => requestAnimationFrame(() => reveal(productInfo, revision)));

      return true;
    },

    cancel(productInfo) {
      nextRevision(productInfo);
      productInfo.classList.remove('bf-gallery-is-loading');
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleTilePreload, { once: true });
  } else {
    scheduleTilePreload();
  }

  document.addEventListener('shopify:section:load', scheduleTilePreload);
})();
// Wait for interior images before leaving the currently visible image.
// Scoped to the opt-in BF gallery; native galleries keep their original behaviour.
(() => {
  const selector = 'media-gallery[data-bf-gallery-active="true"]';
  const pending = new WeakMap();
  const style = document.createElement('style');
  style.textContent = `
    ${selector} [id^="GalleryViewer"] { position: relative; }
    .bf-interior-status { position:absolute; bottom:64px; left:50%; transform:translateX(-50%); z-index:3; display:flex; align-items:center; gap:8px; max-width:90%; padding:9px 13px; border:1px solid #d6dce5; border-radius:5px; background:rgba(255,255,255,.96); color:#43516b; font-size:13px; line-height:1.4; pointer-events:none; box-shadow:0 2px 8px #0000000d; }
    .bf-interior-status[data-loading="true"]::before { content:""; width:13px; height:13px; flex:0 0 13px; border:2px solid #d6dce5; border-top-color:#7e91b3; border-radius:50%; animation:bf-interior-spin .8s linear infinite; }
    @keyframes bf-interior-spin { to { transform:rotate(360deg); } }
    @media (prefers-reduced-motion:reduce) { .bf-interior-status::before { animation:none !important; } }
  `;
  document.head.append(style);

  function cancel(gallery) {
    pending.get(gallery)?.abort();
    pending.delete(gallery);
    gallery.querySelector('.bf-interior-status')?.remove();
    gallery.querySelector('[id^="GalleryViewer"]')?.removeAttribute('aria-busy');
  }

  document.addEventListener('change', (event) => {
    const info = event.target.closest('variant-selects')?.closest('product-info');
    info?.querySelectorAll(selector).forEach(cancel);
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('button');
    const gallery = button?.closest(selector);
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const items = Array.from(viewer?.querySelectorAll('.product__media-item[data-media-id]') || []);
    const thumbnail = button.closest('[data-target]');
    let target = thumbnail && items.find((item) => item.dataset.mediaId === thumbnail.dataset.target);
    if (!target && button.closest('[id^="GalleryViewer"]') && button.classList.contains('slider-button')) {
      const current = Number(viewer.querySelector('.slider-counter--current')?.textContent || 1) - 1;
      const index = pending.get(gallery)?.index ?? current;
      target = items[index + (button.name === 'next' ? 1 : -1)];
    }
    if (!target) return;
    const image = target.querySelector('img');
    if (!image) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    cancel(gallery);
    const info = gallery.closest('product-info');
    const revision = info?.dataset.bfGalleryRevision;
    if (info?.classList.contains('bf-gallery-is-loading')) return;
    if (image.complete && image.naturalWidth > 0) {
      gallery.setActiveMedia(target.dataset.mediaId, false);
      return;
    }
    const status = document.createElement('div');
    status.className = 'bf-interior-status';
    status.dataset.loading = 'true';
    status.setAttribute('role', 'status');
    status.textContent = 'Bild wird geladen …';
    viewer.append(status);
    viewer.setAttribute('aria-busy', 'true');
    let timer;
    let finished = false;
    const valid = () => gallery.isConnected && info?.dataset.bfGalleryRevision === revision;
    const cleanup = () => {
      clearTimeout(timer);
      image.removeEventListener('load', loaded);
      image.removeEventListener('error', failed);
      viewer.removeAttribute('aria-busy');
    };
    const finish = (success) => {
      if (finished) return;
      finished = true;
      cleanup();
      pending.delete(gallery);
      if (!valid()) { status.remove(); return; }
      if (success) {
        status.remove();
        gallery.setActiveMedia(target.dataset.mediaId, false);
      } else {
        status.dataset.loading = 'false';
        status.textContent = 'Bild konnte nicht geladen werden. Bitte erneut wählen.';
      }
    };
    const loaded = () => {
      if (!image.naturalWidth) { finish(false); return; }
      Promise.resolve(image.decode?.()).then(() => finish(true), () => finish(false));
    };
    const failed = () => finish(false);
    pending.set(gallery, {
      index: items.indexOf(target),
      abort() { finished = true; cleanup(); status.remove(); },
    });
    image.addEventListener('load', loaded, { once:true });
    image.addEventListener('error', failed, { once:true });
    timer = setTimeout(failed, 15000);
    image.loading = 'eager';
    image.fetchPriority = 'high';
    if (image.complete && !image.naturalWidth) image.src = image.currentSrc || image.src;
    if (image.complete) image.naturalWidth ? loaded() : failed();
  }, true);
})();
